import {
  DialogContent,
  DialogActions,
  Grid,
  useMediaQuery,
  useTheme,
  useEventCallback,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialog } from 'shell/components/shared/dialog';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { AxiosResponse } from 'axios';
import { useCallback, useState, useMemo } from 'react';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Matricola } from 'shell/models/matricola';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { TableMatricolaVM, headCells } from './head-cells';

export interface ModalMatricolaFormVM {
  surname: string;
  name: string;
  birthday: DateTime | null;
  matricola?: string;
  gender?: string;
}

interface ModalCertificateProps {
  open: boolean;
  onClose: (values?: ModalMatricolaFormVM) => void;
  initialValues?: ModalMatricolaFormVM;
}

const ModalMatricola = ({
  open,
  onClose,
  initialValues,
}: ModalCertificateProps): React.ReactElement => {
  const [matricola, getMatricolas] = useHttpFetch<
    HttpResponseList<Matricola>,
    Array<TableMatricolaVM>
  >(`${__CIVIL_STATUS_API_URL__}/ricerca-matricola`, {
    onBootstrap: false,
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<Matricola>>) => {
      const { data } = response;
      // TODO mapping with search endpoint correct
      return data?.payload?.list?.map((item) => ({
        matricola: item.key.codice?.toString() || '',
        gender: item.sesso,
      })) as Array<TableMatricolaVM>;
    },
  });

  const formContext = useFormik<ModalMatricolaFormVM>({
    initialValues: {
      surname: '',
      name: '',
      birthday: null,
      matricola: '',
      ...(initialValues || {}),
    },
    validationSchema: yup.object().shape({
      surname: yup.string().required('Campo obbligatorio'),
      name: yup.string().required('Campo obbligatorio'),
      matricola: yup.string().required('Campo obbligatorio'),
      birthday: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
    }),
    onSubmit: (values) => {
      onClose({
        ...values,
        surname: values.surname.toUpperCase(),
        name: values.name.toUpperCase(),
      });
    },
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const matricolaList = useMemo(() => {
    const list = (matricola.data || []) as Array<TableMatricolaVM>;

    return list.map((item) => {
      return {
        ...item,
        action: (
          <Radio
            checked={formContext.values.matricola === item.matricola}
            onChange={() => {
              formContext.setValues({
                ...formContext.values,
                matricola: item.matricola,
                gender: item.gender,
              });
            }}
            value={formContext.values.matricola}
            name={`radio-${item.matricola}`}
            inputProps={{ 'aria-label': `Radio ${item.matricola}` }}
          />
        ),
      };
    });
  }, [formContext.values.matricola, formContext.setFieldValue, matricola.data]);

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  const fetchMatricola = useCallback(() => {
    resetPagination();
    getMatricolas({
      data: {
        cognomeNome: `${formContext.values.surname}/${formContext.values.name}`,
        dataNascita: formContext.values.birthday?.toFormat('yyyyMMdd'),
      },
    });
  }, [formContext.values, getMatricolas, resetPagination]);

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  return (
    <PmiDialog
      open={open}
      fullScreen={fullScreen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Selezione Matricola
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2}>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} style={{ paddingTop: '10px' }}>
              <PmiTextField
                aria-label="Cognome"
                label="Cognome"
                id="surname"
                name="surname"
                fullWidth
                value={formContext.values?.surname || ''}
                onChange={(event) => formContext.setFieldValue('surname', event?.target.value)}
                error={formContext.touched?.surname && Boolean(formContext.errors?.surname)}
                helperText={formContext.errors?.surname}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} style={{ paddingTop: '10px' }}>
              <PmiTextField
                aria-label="Nome"
                label="Nome"
                id="name"
                name="name"
                fullWidth
                value={formContext.values?.name || ''}
                onChange={(event) => formContext.setFieldValue('name', event?.target.value)}
                error={formContext.touched?.name && Boolean(formContext.errors?.name)}
                helperText={formContext.errors?.name}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} style={{ paddingTop: '10px' }}>
              <DatePicker
                label="Data nascita"
                aria-label="Data nascita"
                value={formContext.values?.birthday || null}
                onChange={(value) => formContext.setFieldValue('birthday', value)}
                renderInput={(params) => (
                  <PmiTextField
                    id="birthday"
                    name="birthday"
                    {...params}
                    fullWidth
                    error={formContext.touched?.birthday && Boolean(formContext.errors?.birthday)}
                    helperText={formContext.errors?.birthday}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} style={{ paddingTop: '10px' }}>
              <Tooltip
                arrow
                title={
                  !!formContext.errors.birthday ||
                  !!formContext.errors.name ||
                  !!formContext.errors.surname
                    ? 'Inserire campi di ricerca'
                    : ''
                }
              >
                <span>
                  <PmiButton
                    variant="outlined"
                    color="cdmBrandThird"
                    onClick={() => {
                      fetchMatricola();
                    }}
                    disabled={
                      !!formContext.errors.birthday ||
                      !!formContext.errors.name ||
                      !!formContext.errors.surname
                    }
                  >
                    Cerca
                  </PmiButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {matricola.data ? (
              matricolaList.length > 0 ? (
                <Table
                  ariaLabel="Lista matricole"
                  page={paginationState.page}
                  rowsPerPage={paginationState.rowsPerPage}
                  body={matricolaList}
                  count={matricolaList.length}
                  head={headCells}
                  onSort={noop}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  onVisibilityPress={noop}
                  orders={{}}
                  minWidth={10}
                  noSort
                  hideButtons
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessun elemento trovato con la ricerca effettuata.
                </Typography>
              )
            ) : undefined}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <PmiButton
          variant="contained"
          color="primary"
          type="submit"
          onClick={() => formContext.handleSubmit()}
        >
          Conferma
        </PmiButton>
        <PmiButton variant="outlined" color="primary" type="button" onClick={() => onClose()}>
          Chiudi
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalMatricola.displayName = 'Modal matricola';

export default ModalMatricola;
